import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Restore } from '@mui/icons-material';
import { caseStatusAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface CaseStatusRow {
  id: number;
  code: string;
  name: string;
  sort_order: number;
  is_default: boolean;
  is_system: boolean;
  is_removed: boolean;
  project_type_count: number;
  case_count: number;
}

interface ProjectTypeRow {
  id: number;
  name: string;
  status_ids: number[];
}

const CaseStatuses: React.FC = () => {
  const { user } = useAuth();
  const canManage = (user as any)?.role === 'site_admin'
    || !!user?.roles?.some((r: any) => r.slug === 'pmp_admin' || r.slug === 'site_admin');

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [statuses, setStatuses] = useState<CaseStatusRow[]>([]);
  const [projectTypes, setProjectTypes] = useState<ProjectTypeRow[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<number | ''>('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEditId, setDialogEditId] = useState<number | null>(null);
  const [dialogName, setDialogName] = useState('');
  const [dialogSortOrder, setDialogSortOrder] = useState('');
  const [dialogSaving, setDialogSaving] = useState(false);

  const [newTypeStatusName, setNewTypeStatusName] = useState('');

  const notify = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const errorMessage = (e: any, fallback: string) => {
    const errors = e?.response?.data?.errors;
    const first = errors ? (Object.values(errors).flat()[0] as string | undefined) : undefined;
    return first || e?.response?.data?.message || fallback;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const res = await caseStatusAPI.getAll();
      const data = res.data.data || {};
      setStatuses(data.statuses || []);
      const types: ProjectTypeRow[] = data.project_types || [];
      setProjectTypes(types);
      setSelectedTypeId((prev) => (prev !== '' && types.some((t) => t.id === prev) ? prev : (types[0]?.id ?? '')));
    } catch (e: any) {
      setLoadError(errorMessage(e, 'Failed to load case statuses'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeStatuses = useMemo(() => statuses.filter((s) => !s.is_removed), [statuses]);
  const selectedType = projectTypes.find((t) => t.id === selectedTypeId) || null;

  const runAction = async (key: string, action: () => Promise<any>, success: string, fallback: string) => {
    try {
      setBusyKey(key);
      await action();
      notify(success);
      await loadData();
    } catch (e: any) {
      notify(errorMessage(e, fallback), 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const openAdd = () => {
    setDialogEditId(null);
    setDialogName('');
    setDialogSortOrder('');
    setDialogOpen(true);
  };

  const openEdit = (s: CaseStatusRow) => {
    setDialogEditId(s.id);
    setDialogName(s.name);
    setDialogSortOrder(String(s.sort_order));
    setDialogOpen(true);
  };

  const handleSaveDialog = async () => {
    const name = dialogName.trim();
    if (!name) {
      notify('Name is required', 'error');
      return;
    }
    try {
      setDialogSaving(true);
      if (dialogEditId != null) {
        await caseStatusAPI.update(dialogEditId, {
          name,
          sort_order: dialogSortOrder === '' ? undefined : parseInt(dialogSortOrder, 10),
        });
        notify('Status updated');
      } else {
        await caseStatusAPI.create({ name });
        notify('Status added to the default list');
      }
      setDialogOpen(false);
      await loadData();
    } catch (e: any) {
      notify(errorMessage(e, 'Failed to save status'), 'error');
    } finally {
      setDialogSaving(false);
    }
  };

  const handleRemove = (s: CaseStatusRow) => {
    const usage = s.case_count > 0 ? ` ${s.case_count} case(s) currently use it and will keep it.` : '';
    if (!window.confirm(`Remove "${s.name}"? It will be hidden from every status dropdown.${usage}`)) return;
    runAction(`remove-${s.id}`, () => caseStatusAPI.remove(s.id), 'Status removed', 'Failed to remove status');
  };

  const toggleMapping = (statusId: number, mapped: boolean) => {
    if (!selectedType) return;
    runAction(
      `map-${selectedType.id}-${statusId}`,
      () => (mapped
        ? caseStatusAPI.detach(selectedType.id, statusId)
        : caseStatusAPI.attach(selectedType.id, statusId)),
      mapped ? 'Status removed from this project type' : 'Status added to this project type',
      'Failed to update project type'
    );
  };

  const handleAddForType = () => {
    const name = newTypeStatusName.trim();
    if (!name || !selectedType) return;
    runAction(
      `add-type-${selectedType.id}`,
      async () => {
        await caseStatusAPI.create({ name, project_type_id: selectedType.id });
        setNewTypeStatusName('');
      },
      `Status added for ${selectedType.name}`,
      'Failed to add status'
    );
  };

  if (!canManage) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">Only PMP admins or Site admins can manage case statuses.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>Case Statuses</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every status lives in one master list. Each project type (matched by project name) uses the statuses mapped to it.
        A project type with no mapped statuses uses the default list. Removing never deletes data: cases already on a removed status keep it.
      </Typography>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      <Paper>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Status master" />
          <Tab label="By project type" />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : tab === 0 ? (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add default status</Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="center">Sort</TableCell>
                    <TableCell align="center">In default list</TableCell>
                    <TableCell align="center">Project types</TableCell>
                    <TableCell align="center">Cases</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statuses.map((s) => (
                    <TableRow key={s.id} sx={s.is_removed ? { opacity: 0.55 } : undefined}>
                      <TableCell>
                        {s.name}
                        {s.is_system && <Chip label="System" size="small" sx={{ ml: 1 }} />}
                        {s.is_removed && <Chip label="Removed" size="small" color="warning" sx={{ ml: 1 }} />}
                        {!s.is_default && !s.is_removed && <Chip label="Project only" size="small" variant="outlined" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell><code>{s.code}</code></TableCell>
                      <TableCell align="center">{s.sort_order}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={s.is_system ? 'System statuses are always in the default list' : ''}>
                          <span>
                            <Switch
                              size="small"
                              checked={s.is_default}
                              disabled={s.is_system || s.is_removed || busyKey !== null}
                              onChange={(e) => runAction(
                                `default-${s.id}`,
                                () => caseStatusAPI.update(s.id, { is_default: e.target.checked }),
                                'Default list updated',
                                'Failed to update status'
                              )}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">{s.project_type_count}</TableCell>
                      <TableCell align="center">{s.case_count}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(s)} disabled={busyKey !== null} aria-label="Edit status">
                          <Edit fontSize="small" />
                        </IconButton>
                        {s.is_removed ? (
                          <Tooltip title="Restore">
                            <IconButton
                              size="small"
                              disabled={busyKey !== null}
                              onClick={() => runAction(`restore-${s.id}`, () => caseStatusAPI.restore(s.id), 'Status restored', 'Failed to restore status')}
                            >
                              <Restore fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={s.is_system ? 'Used by delivery, reopen, and auto-close; cannot be removed' : 'Remove'}>
                            <span>
                              <IconButton size="small" disabled={s.is_system || busyKey !== null} onClick={() => handleRemove(s)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            {projectTypes.length === 0 ? (
              <Alert severity="info">No project types yet.</Alert>
            ) : (
              <>
                <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
                  <InputLabel>Project type</InputLabel>
                  <Select
                    label="Project type"
                    value={selectedTypeId}
                    onChange={(e) => setSelectedTypeId(Number(e.target.value))}
                  >
                    {projectTypes.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedType && (
                  <>
                    {selectedType.status_ids.length === 0 && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No statuses are mapped, so cases in "{selectedType.name}" projects use the default list.
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mb: 2, maxWidth: 520 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label={`Add a status for ${selectedType.name} only`}
                        value={newTypeStatusName}
                        onChange={(e) => setNewTypeStatusName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddForType();
                          }
                        }}
                        helperText="Added to the master list and mapped only to this project type. An existing name is reused."
                      />
                      <Button
                        variant="contained"
                        onClick={handleAddForType}
                        disabled={!newTypeStatusName.trim() || busyKey !== null}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Add
                      </Button>
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">Mapped</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Code</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeStatuses.map((s) => {
                            const mapped = selectedType.status_ids.includes(s.id);
                            return (
                              <TableRow key={s.id} hover>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    checked={mapped}
                                    disabled={busyKey !== null}
                                    onChange={() => toggleMapping(s.id, mapped)}
                                  />
                                </TableCell>
                                <TableCell>
                                  {s.name}
                                  {!s.is_default && <Chip label="Project only" size="small" variant="outlined" sx={{ ml: 1 }} />}
                                </TableCell>
                                <TableCell><code>{s.code}</code></TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !dialogSaving && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{dialogEditId != null ? 'Edit status' : 'Add default status'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Name"
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            helperText={dialogEditId != null ? 'Renaming changes the label only. The stored code stays the same.' : 'Added to the default list. Map it to project types on the next tab.'}
          />
          {dialogEditId != null && (
            <TextField
              fullWidth
              margin="dense"
              type="number"
              label="Sort order"
              value={dialogSortOrder}
              onChange={(e) => setDialogSortOrder(e.target.value)}
              inputProps={{ min: 0 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={dialogSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDialog} disabled={dialogSaving}>
            {dialogSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CaseStatuses;
